/**
 * useTreeMapLayout Hook
 *
 * Transforms hierarchical sphere data into a flat grid-based layout structure.
 * Each first-level child becomes its own swim lane with vertical stacking.
 */

import { useMemo } from 'react';
import type { Sphere, TreeMapTemplate } from './types';
import { expandLevelsWithRepeat } from './types';

// ============================================================================
// Types
// ============================================================================

export interface GridNode {
  /** The sphere data */
  sphere: Sphere;

  /** Column index in the grid (0 = root, 1 = first level, etc.) */
  column: number;

  /** Starting row in the grid (1-indexed) */
  rowStart: number;

  /** Number of rows this node spans */
  rowSpan: number;

  /** Parent sphere ID for drawing connections */
  parentId?: string;

  /** Child sphere IDs */
  children: string[];

  /** Whether this node has been expanded (has loaded children) */
  isExpanded: boolean;

  /** Swim lane ID this node belongs to */
  swimLaneId: string;

  /** Color index for hierarchical color coding (0-4) */
  colorIndex: number;

  /** Internal flag: Whether this node shows a footer row (set during layout recalculation) */
  __showsFooter?: boolean;
}

export interface SwimLane {
  /** Unique identifier (typically first-level child sphere ID) */
  id: string;

  /** Display label (first-level child name) */
  label: string;

  /** Starting row in the grid */
  rowStart: number;

  /** Number of rows this lane spans */
  rowSpan: number;

  /** Map of nodes in this lane by column index */
  nodes: Map<number, GridNode[]>;

  /** Parent sphere for this swim lane */
  parentSphere: Sphere;

  /** Internal flag: Whether this swim lane shows a footer row (set during layout recalculation) */
  __showsFooter?: boolean;
}

export interface TreeMapLayout {
  /** All nodes keyed by sphere ID */
  nodes: Map<string, GridNode>;

  /** Total number of rows in the grid */
  totalRows: number;

  /** Total number of columns (levels) in the grid */
  totalColumns: number;

  /** Swim lanes (one per first-level child) */
  swimLanes: SwimLane[];

  /** Nodes organized by column for easy access */
  nodesByColumn: Map<number, GridNode[]>;
}

// ============================================================================
// Layout Computation
// ============================================================================

function computeLayout(
  spheresByColumn: Record<number, Sphere[]>,
  template: TreeMapTemplate,
  rootTemplateLevel: number
): TreeMapLayout {
  console.log('🔄 [Layout] ============ COMPUTE LAYOUT START ============');
  console.log('🔄 [Layout] Spheres by column:', Object.entries(spheresByColumn).map(([col, spheres]) => `Col ${col}: ${spheres.length} spheres`));

  // Log detailed sphere state to detect duplicates
  logSpheresState(spheresByColumn);

  const nodes = new Map<string, GridNode>();
  const swimLanes: SwimLane[] = [];
  const nodesByColumn = new Map<number, GridNode[]>();

  let currentRow = 1;

  // Calculate total columns from TEMPLATE structure (not just data)
  // This ensures we create columns for all template levels, even if some have no data yet
  // IMPORTANT: Use expanded levels count to account for repeat property
  // In root view: totalColumns = expandedLevels.length (e.g., 4 for template with [{repeat:1}, {repeat:3}])
  // In focused view: totalColumns = expandedLevels.length - rootTemplateLevel (remaining levels after focus)
  const expandedLevels = expandLevelsWithRepeat(template.levels);
  const totalColumns = rootTemplateLevel === 0
    ? expandedLevels.length
    : Math.max(0, expandedLevels.length - rootTemplateLevel);

  const actualColumns = Object.keys(spheresByColumn).map(Number);
  const maxColumnFromData = actualColumns.length > 0 ? Math.max(...actualColumns) : 0;

  console.log(`🔢 [Layout] Total columns from template: ${totalColumns}, max column with data: ${maxColumnFromData} (actual columns: [${actualColumns.join(', ')}])`);

  // Get root sphere (column 0)
  const rootSpheres = spheresByColumn[0] || [];
  if (rootSpheres.length === 0) {
    return { nodes, totalRows: 0, totalColumns: totalColumns + 1, swimLanes, nodesByColumn };
  }

  const rootSphere = rootSpheres[0]; // Assume single root

  // Get first level children (column 1) - these become swim lanes
  // Could be Pillars, Priorities, Strategies, etc. depending on focus level
  const firstLevelChildren = spheresByColumn[1] || [];

  console.log(`🏊 [Layout] First level children (swim lanes): ${firstLevelChildren.length} items`);

  // Create root node - will span all swim lanes
  const rootNode: GridNode = {
    sphere: rootSphere,
    column: 0,
    rowStart: 1,
    rowSpan: 1, // Will be updated after processing all swim lanes
    children: firstLevelChildren.map(p => p.id),
    isExpanded: true,
    swimLaneId: rootSphere.id,
    colorIndex: 0,
  };

  nodes.set(rootSphere.id, rootNode);
  if (!nodesByColumn.has(0)) {
    nodesByColumn.set(0, []);
  }
  nodesByColumn.get(0)!.push(rootNode);

  // Process each first-level child as its own swim lane
  for (let childIndex = 0; childIndex < firstLevelChildren.length; childIndex++) {
    const firstLevelChild = firstLevelChildren[childIndex];
    const swimLaneStartRow = currentRow;
    const swimLaneNodes = new Map<number, GridNode[]>();
    const colorIndex = childIndex % 5;

    // Add root to this swim lane's node map
    if (!swimLaneNodes.has(0)) {
      swimLaneNodes.set(0, []);
    }
    swimLaneNodes.get(0)!.push(rootNode);

    // Create first-level child node (swim lane header)
    const swimLaneHeaderNode: GridNode = {
      sphere: firstLevelChild,
      column: 1,
      rowStart: currentRow,
      rowSpan: 1, // Will be updated
      parentId: rootSphere.id,
      children: [],
      isExpanded: false,
      swimLaneId: firstLevelChild.id,
      colorIndex,
    };

    nodes.set(firstLevelChild.id, swimLaneHeaderNode);
    if (!nodesByColumn.has(1)) {
      nodesByColumn.set(1, []);
    }
    nodesByColumn.get(1)!.push(swimLaneHeaderNode);
    if (!swimLaneNodes.has(1)) {
      swimLaneNodes.set(1, []);
    }
    swimLaneNodes.get(1)!.push(swimLaneHeaderNode);

    // Process all deeper levels (2, 3, 4, ...) within this swim lane
    for (let level = 2; level <= totalColumns; level++) {
      const parentLevel = level - 1;
      const parentNodesInLane = swimLaneNodes.get(parentLevel) || [];
      const allSpheresAtLevel = spheresByColumn[level] || [];

      if (!swimLaneNodes.has(level)) {
        swimLaneNodes.set(level, []);
      }

      // Level 2: Second-level children - Individual cards stacked vertically
      if (level === 2) {
        // For each parent in this swim lane, find and add its children
        for (const parentNode of parentNodesInLane) {
          const childrenOfParent = allSpheresAtLevel.filter(
            child => child.parent_id === parentNode.sphere.id
          );

          if (childrenOfParent.length === 0) continue;

          // Process each child individually - each gets its own row
          for (const childSphere of childrenOfParent) {
            // Look ahead to see how many grandchildren this child has
            const grandchildLevel = level + 1;
            const grandchildrenCount = grandchildLevel <= totalColumns
              ? (spheresByColumn[grandchildLevel] || []).filter(gc => gc.parent_id === childSphere.id).length
              : 0;

            // Calculate rowSpan as number of grandchildren (each grandchild is 1 row)
            const childRowSpan = grandchildrenCount > 0 ? grandchildrenCount : 1;

            const childNode: GridNode = {
              sphere: childSphere,
              column: level,
              rowStart: currentRow,
              rowSpan: childRowSpan,
              parentId: parentNode.sphere.id,
              children: [],
              isExpanded: false,
              swimLaneId: firstLevelChild.id,
              colorIndex,
            };

            nodes.set(childSphere.id, childNode);
            parentNode.children.push(childSphere.id);

            if (!nodesByColumn.has(level)) {
              nodesByColumn.set(level, []);
            }
            nodesByColumn.get(level)!.push(childNode);
            swimLaneNodes.get(level)!.push(childNode);

            // Move to next row for next child
            currentRow += childRowSpan;
          }
        }
      }
      // Level 3: Third-level children - Individual cards stacked within parent's span
      else if (level === 3) {
        // For each parent in this swim lane
        for (const parentNode of parentNodesInLane) {
          const childrenOfParent = allSpheresAtLevel.filter(
            child => child.parent_id === parentNode.sphere.id
          );

          console.log(`🔍 [Layout] Parent "${parentNode.sphere.properties.name}" (ID: ${parentNode.sphere.id.substring(0,8)}): Found ${childrenOfParent.length} children out of ${allSpheresAtLevel.length} total spheres at level ${level}`);

          if (childrenOfParent.length === 0) continue;

          // Start children at parent's rowStart
          let childRow = parentNode.rowStart;

          // Process each child individually - each gets exactly 1 row (no gaps)
          console.log(`🎯 [Layout] Positioning ${childrenOfParent.length} level-${level} children for parent "${parentNode.sphere.properties.name}"`);
          console.log(`🎯 [Layout]   Parent rowStart=${parentNode.rowStart}, rowSpan=${parentNode.rowSpan}`);
          console.log(`🎯 [Layout]   Starting child placement at row ${childRow}`);

          for (let i = 0; i < childrenOfParent.length; i++) {
            const childSphere = childrenOfParent[i];
            // Level 3 children are always 1 row - deeper containers handle their own sizing
            const childRowSpan = 1;

            const childNode: GridNode = {
              sphere: childSphere,
              column: level,
              rowStart: childRow,
              rowSpan: childRowSpan,
              parentId: parentNode.sphere.id,
              children: [],
              isExpanded: false,
              swimLaneId: firstLevelChild.id,
              colorIndex,
            };

            console.log(`  ↳ [${i+1}/${childrenOfParent.length}] Child "${childSphere.properties.name}" (ID: ${childSphere.id.substring(0,8)}) at row ${childRow}`);

            nodes.set(childSphere.id, childNode);
            parentNode.children.push(childSphere.id);

            if (!nodesByColumn.has(level)) {
              nodesByColumn.set(level, []);
            }
            nodesByColumn.get(level)!.push(childNode);
            swimLaneNodes.get(level)!.push(childNode);

            // Move to next row for next child (always 1 row)
            childRow += 1;
          }

          console.log(`🎯 [Layout]   Finished positioning children. Last child ended at row ${childRow - 1}`);
          console.log(`🎯 [Layout]   Parent "${parentNode.sphere.properties.name}" currently has rowStart=${parentNode.rowStart}, rowSpan=${parentNode.rowSpan}`);
          console.log(`🎯 [Layout]   Expected parent should span rows ${parentNode.rowStart} to ${childRow - 1} (${childRow - parentNode.rowStart} rows total)`);

          // Don't increment currentRow - children are positioned within parent's span
        }
      }
      // Level 4+: Deeper children - Flexbox containers aligned with parent
      else {
        // For each parent in this swim lane, find and add its children as a group
        for (const parentNode of parentNodesInLane) {
          const childrenOfParent = allSpheresAtLevel.filter(
            child => child.parent_id === parentNode.sphere.id
          );

          if (childrenOfParent.length === 0) continue;

          // Use parent's position and span - deeper children align with their parent
          const containerStartRow = parentNode.rowStart;
          const containerRows = parentNode.rowSpan;

          // Add all children with the same rowStart (they'll be in a flexbox container)
          for (const childSphere of childrenOfParent) {
            const childNode: GridNode = {
              sphere: childSphere,
              column: level,
              rowStart: containerStartRow,
              rowSpan: containerRows,
              parentId: parentNode.sphere.id,
              children: [],
              isExpanded: false,
              swimLaneId: firstLevelChild.id,
              colorIndex,
            };

            nodes.set(childSphere.id, childNode);
            parentNode.children.push(childSphere.id);

            if (!nodesByColumn.has(level)) {
              nodesByColumn.set(level, []);
            }
            nodesByColumn.get(level)!.push(childNode);
            swimLaneNodes.get(level)!.push(childNode);
          }

          // Don't increment currentRow - deeper children inherit parent's position
        }
      }
    }

    // IMPORTANT: If the swim lane has no children (currentRow never advanced),
    // we need to ensure it still occupies sufficient rows to show the add button properly
    // Give it a minimum height that matches the smallest child-containing swim lane
    const MIN_EMPTY_SWIMLANE_ROWS = 1; // Minimum rows for an empty swim lane (just enough for the header)

    if (currentRow === swimLaneStartRow) {
      console.log(`⚠️ [Layout] Swim lane "${firstLevelChild.properties.name}" has no children, assigning minimum height of ${MIN_EMPTY_SWIMLANE_ROWS} rows`);
      currentRow += MIN_EMPTY_SWIMLANE_ROWS; // Advance by minimum rows
    }

    // Calculate swim lane row span after potentially adjusting currentRow
    const swimLaneEndRow = currentRow - 1;
    const swimLaneRowSpan = Math.max(1, swimLaneEndRow - swimLaneStartRow + 1);
    swimLaneHeaderNode.rowSpan = swimLaneRowSpan;

    swimLanes.push({
      id: firstLevelChild.id,
      label: firstLevelChild.properties.name || 'Untitled',
      rowStart: swimLaneStartRow,
      rowSpan: swimLaneRowSpan,
      nodes: swimLaneNodes,
      parentSphere: firstLevelChild,
    });
  }

  // Update root node to span all swim lanes
  const totalRows = currentRow - 1;
  rootNode.rowSpan = totalRows;

  console.log('🔄 [Layout] Layout computed:', {
    totalRows,
    totalColumns: totalColumns + 1,
    swimLanes: swimLanes.length,
    totalNodes: nodes.size
  });

  // Recalculate rowSpans to ensure parents span all their children
  recalculateRowSpans(nodes);

  // Log final node positions for debugging
  logFinalNodePositions(nodes);

  return {
    nodes,
    totalRows,
    totalColumns: totalColumns + 1, // +1 for root column
    swimLanes,
    nodesByColumn,
  };
}

/**
 * Recalculate rowSpans for all parent nodes dynamically.
 * Processes columns in reverse order (deepest to root) to ensure
 * children are calculated before parents.
 * Ensures each parent spans the total height of all its children.
 */
function recalculateRowSpans(nodes: Map<string, GridNode>): void {
  console.log('🔄 [Layout] ============ RECALCULATE ROW SPANS START ============');
  console.log('🔄 [Layout] Total nodes in map:', nodes.size);

  // Find all unique columns in the data
  const columns = Array.from(new Set(Array.from(nodes.values()).map(n => n.column))).sort((a, b) => b - a);
  console.log(`🔄 [Layout] Recalculating rowSpans for columns: [${columns.join(', ')}]`);

  // Recalculate from deepest column back to root
  // Process in reverse order so children are calculated before parents
  for (const column of columns) {
    const nodesInColumn = Array.from(nodes.values()).filter(node => node.column === column);
    console.log(`🔄 [Layout] Column ${column}: ${nodesInColumn.length} nodes`);

    for (const node of nodesInColumn) {
      if (node.children.length > 0) {
        console.log(`  🔍 Processing "${node.sphere.properties.name}" with ${node.children.length} children`);

        let minChildRow = Infinity;
        let maxChildRow = -Infinity;

        for (const childId of node.children) {
          const child = nodes.get(childId);
          if (child) {
            const childEndRow = child.rowStart + child.rowSpan - 1;
            console.log(`    ↳ Child "${child.sphere.properties.name}": rowStart=${child.rowStart}, rowSpan=${child.rowSpan}, endRow=${childEndRow}`);
            minChildRow = Math.min(minChildRow, child.rowStart);
            maxChildRow = Math.max(maxChildRow, childEndRow);
          }
        }

        if (minChildRow !== Infinity && maxChildRow !== -Infinity) {
          const oldRowSpan = node.rowSpan;
          node.rowSpan = maxChildRow - node.rowStart + 1;
          if (oldRowSpan === node.rowSpan) {
            console.log(`  📐 ${node.sphere.properties.name}: rowSpan unchanged ${node.rowSpan} (covers rows ${node.rowStart}-${maxChildRow})`);
          } else {
            console.log(`  📐 ${node.sphere.properties.name}: rowSpan CHANGED ${oldRowSpan} → ${node.rowSpan} (covers rows ${node.rowStart}-${maxChildRow})`);
          }
        }
      }
    }
  }
  console.log('🔄 [Layout] ============ RECALCULATE ROW SPANS END ============');
}

/**
 * Diagnostic function to log the complete state of spheresByColumn.
 * Helps identify duplication and ordering issues.
 */
function logSpheresState(spheresByColumn: Record<number, Sphere[]>): void {
  console.log('📊 [Layout] ============ SPHERES BY COLUMN STATE ============');
  for (const [column, spheres] of Object.entries(spheresByColumn)) {
    console.log(`📊 [Layout] Column ${column}: ${spheres.length} spheres`);

    if (Number.parseInt(column) === 2 || Number.parseInt(column) === 3) {
      // Log details for level 2 and 3 children to detect duplicates
      const sphereIds = new Set<string>();
      const duplicates: string[] = [];

      spheres.forEach((sphere, index) => {
        if (sphereIds.has(sphere.id)) {
          duplicates.push(sphere.id);
        }
        sphereIds.add(sphere.id);

        console.log(`  [${index}] "${sphere.properties.name}" (ID: ${sphere.id.substring(0,8)}...) parent: ${sphere.parent_id?.substring(0,8)}...`);
      });

      if (duplicates.length > 0) {
        console.warn(`⚠️ [Layout] DUPLICATES FOUND in column ${column}:`, duplicates.map(id => id.substring(0,8)));
      }
    }
  }
  console.log('📊 [Layout] ============ END SPHERES STATE ============');
}

/**
 * Log final node positions after layout computation.
 * Shows the actual grid positions for all child levels.
 */
function logFinalNodePositions(nodes: Map<string, GridNode>): void {
  console.log('🎯 [Layout] ============ FINAL NODE POSITIONS ============');

  // Group nodes by column
  const nodesByColumn = new Map<number, GridNode[]>();
  nodes.forEach(node => {
    if (!nodesByColumn.has(node.column)) {
      nodesByColumn.set(node.column, []);
    }
    nodesByColumn.get(node.column)!.push(node);
  });

  // Log columns 2 and 3 (second and third child levels)
  [2, 3].forEach(column => {
    const nodesInColumn = nodesByColumn.get(column) || [];
    if (nodesInColumn.length === 0) return;

    console.log(`🎯 [Layout] Column ${column}: ${nodesInColumn.length} nodes`);

    // Sort by rowStart for easier reading
    const sorted = [...nodesInColumn].sort((a, b) => a.rowStart - b.rowStart);

    sorted.forEach((node, index) => {
      const endRow = node.rowStart + node.rowSpan - 1;
      console.log(`  [${index}] "${node.sphere.properties.name}" (ID: ${node.sphere.id.substring(0,8)}) row ${node.rowStart}-${endRow} (span=${node.rowSpan}) parent: ${node.parentId?.substring(0,8)}`);
    });

    // Check for gaps or overlaps
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const prevEnd = prev.rowStart + prev.rowSpan - 1;
      const gap = curr.rowStart - prevEnd - 1;

      if (gap > 0) {
        console.warn(`⚠️ [Layout] GAP DETECTED: ${gap} rows between "${prev.sphere.properties.name}" (ends at ${prevEnd}) and "${curr.sphere.properties.name}" (starts at ${curr.rowStart})`);
      } else if (gap < 0) {
        console.warn(`⚠️ [Layout] OVERLAP DETECTED: "${prev.sphere.properties.name}" (ends at ${prevEnd}) overlaps "${curr.sphere.properties.name}" (starts at ${curr.rowStart})`);
      }
    }
  });

  console.log('🎯 [Layout] ============ END FINAL POSITIONS ============');
}

// ============================================================================
// Hook
// ============================================================================

export function useTreeMapLayout({
  spheresByColumn,
  template,
  rootTemplateLevel = 0,
}: {
  spheresByColumn: Record<number, Sphere[]>;
  template: TreeMapTemplate | null;
  rootTemplateLevel?: number;
}) {
  const layout = useMemo(() => {
    if (!template || Object.keys(spheresByColumn).length === 0) {
      return {
        nodes: new Map<string, GridNode>(),
        totalRows: 0,
        totalColumns: 0,
        swimLanes: [],
        nodesByColumn: new Map<number, GridNode[]>(),
      };
    }

    return computeLayout(spheresByColumn, template, rootTemplateLevel);
  }, [spheresByColumn, template, rootTemplateLevel]);

  return { layout };
}
